import { DateTime } from "luxon";

import { AssignedTo } from "@/dashboard/features/hub/lib/customer/assignments/schemas";
import { CustomerDetail } from "@/dashboard/features/hub/lib/customer/schemas";

const CUSTOMER_DETAIL_REPLICA_GUARD_DURATION_SECONDS = 60;

export type CustomerDetailReplicaGuard = {
  assignedTo: AssignedTo | null;
  expiresAt: string;
};

export const customerDetailReplicaGuardQueryKey = (customerId: string) =>
  ["customer-detail-replica-guard", customerId] as const;

export function createCustomerDetailReplicaGuard(
  assignedTo: AssignedTo | undefined
): CustomerDetailReplicaGuard {
  const expiresAt = DateTime.utc()
    .plus({ seconds: CUSTOMER_DETAIL_REPLICA_GUARD_DURATION_SECONDS })
    .toISO();
  if (!expiresAt) {
    throw new Error("Unable to create customer detail replica guard");
  }
  return { assignedTo: assignedTo ?? null, expiresAt };
}

function assignmentsMatch(left: AssignedTo | undefined, right: AssignedTo | null): boolean {
  if (!left || !right) {
    return !left && !right;
  }
  return left.type === right.type && left.value.id === right.value.id;
}

export function customerDetailReplicaGuardIsExpired(
  guard: CustomerDetailReplicaGuard,
  now = DateTime.utc()
): boolean {
  return DateTime.fromISO(guard.expiresAt).toMillis() <= now.toMillis();
}

export function customerDetailReplicaGuardIsSatisfied(
  customer: Pick<CustomerDetail, "assigned_to">,
  guard: CustomerDetailReplicaGuard
): boolean {
  return assignmentsMatch(customer.assigned_to, guard.assignedTo);
}

export function applyCustomerDetailReplicaGuard<T extends Pick<CustomerDetail, "assigned_to">>(
  customer: T,
  guard: CustomerDetailReplicaGuard | undefined,
  now = DateTime.utc()
): T {
  if (!guard || customerDetailReplicaGuardIsExpired(guard, now)) {
    return customer;
  }

  if (customerDetailReplicaGuardIsSatisfied(customer, guard)) {
    return customer;
  }

  return {
    ...customer,
    assigned_to: guard.assignedTo ?? undefined,
  };
}
